import React from 'react'
import { CheckCircle, Shield, Target, Rocket, Users, Award } from 'lucide-react'

export default function Whywork() {
    const getIcon = (index) => {
        const icons = [Target, Shield, Rocket];
        return icons[index] || CheckCircle;
    };

    return (
        <section className='py-20 w-full bg-gradient-to-br from-blue-50 to-indigo-100'>
            <div className="bl-container">
                {/* Header */}
                <div className="text-center mb-16">
                    <h2 className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'>
                        Why Work With Us
                    </h2>
                    <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        We deliver exceptional results through our proven methodology and commitment to excellence
                    </p>
                </div>

                {/* Main Features Grid */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-16'>
                    {data.map((item, index) => {
                        const IconComponent = getIcon(index);
                        const colors = [
                            { bg: 'from-blue-500 to-blue-600', icon: 'text-blue-600', accent: 'bg-blue-50' },
                            { bg: 'from-green-500 to-green-600', icon: 'text-green-600', accent: 'bg-green-50' },
                            { bg: 'from-purple-500 to-purple-600', icon: 'text-purple-600', accent: 'bg-purple-50' }
                        ];
                        const color = colors[index] || colors[0];

                        return (
                            <div 
                                key={index} 
                                className='group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 border border-gray-100 relative overflow-hidden'
                            >
                                {/* Background Pattern */}
                                <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                                    <div className={`w-full h-full bg-gradient-to-br ${color.bg} rounded-full transform translate-x-16 -translate-y-16`}></div>
                                </div>

                                {/* Icon */}
                                <div className="flex justify-center mb-6">
                                    <div className={`p-4 ${color.accent} rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                                        <IconComponent size={40} className={`${color.icon}`} />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="text-center space-y-4">
                                    <h3 className='text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300'>
                                        {item.text}
                                    </h3>
                                    <p className='text-gray-600 leading-relaxed'>
                                        {item.description}
                                    </p>
                                </div>

                                {/* Hover Border Effect */}
                                <div className={`absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r ${color.bg} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
                            </div>
                        )
                    })}
                </div>

                {/* Additional Benefits */}
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                    <div className="text-center mb-8">
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">Additional Benefits</h3>
                        <p className="text-gray-600 max-w-xl mx-auto">
                            When you choose us, you get more than just a service provider - you get a dedicated partner
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Users,
                                title: "Dedicated Team",
                                description: "Expert professionals committed to your success"
                            },
                            {
                                icon: Award,
                                title: "Quality Assurance", 
                                description: "Rigorous testing and quality control processes"
                            },
                            {
                                icon: CheckCircle,
                                title: "24/7 Support",
                                description: "Round-the-clock assistance whenever you need it"
                            }
                        ].map((benefit, index) => {
                            const IconComponent = benefit.icon;
                            return (
                                <div key={index} className="text-center p-6 rounded-xl hover:bg-gray-50 transition-colors duration-300">
                                    <div className="flex justify-center mb-4">
                                        <IconComponent size={32} className="text-blue-600" />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                                    <p className="text-gray-600">{benefit.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* 
                <div className="text-center mt-16">
                    <p className="text-gray-600 mb-6">
                        Ready to experience the difference?
                    </p>
                    <button className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl">
                        Start Your Project
                        <Rocket className="ml-2 w-5 h-5" />
                    </button>
                </div> */}
            </div>
        </section>
    )
}

export const data = [
    {
        text: 'End-to-End Delivery',
        description: 'Complete solutions from design to deployment, ensuring seamless integration and delivery.',
    },
    {
        text: 'Production-Ready Apps',
        description: 'Secure architecture and robust systems built for real-world applications.',
    },
    {
        text: 'Product-Focused',
        description: 'Beyond just code - we focus on delivering complete product solutions that drive business value.',
    },
]